import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';

  const filteredArticles = useMemo(() => {
    if (currentSlug === 'all') return articles;
    return articles.filter((a) => a.categorySlug === currentSlug);
  }, [currentSlug]);

  const currentCategory = categories.find((c) => c.slug === currentSlug);

  return (
    <>
      <Navbar />

      <main>
        {/* Breadcrumb & Header */}
        <section className="w-full bg-background-100 pt-24 pb-8 md:pt-28 md:pb-12">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-foreground-600 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-600 font-medium">
                {currentCategory ? currentCategory.name : '全部文章'}
              </span>
            </nav>

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {currentCategory ? currentCategory.name : '全部文章'}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base">
              {currentCategory ? currentCategory.description : '浏览所有事奉装备资源'}
            </p>
          </div>
        </section>

        {/* Category Tabs + Article List */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            {/* Tabs */}
            <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    currentSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Article Count */}
            <p className="text-sm text-foreground-400 mb-6">
              共 <strong className="text-foreground-700">{filteredArticles.length}</strong> 篇文章
            </p>

            {/* Article Grid */}
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group rounded-xl bg-background-100 overflow-hidden hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        title={`${article.title} - 牧教师`}
                      />
                    </div>
                    <div className="p-5">
                      <span className="inline-block px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-3 whitespace-nowrap">
                        {article.category}
                      </span>
                      <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span>{article.author}</span>
                        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                        <span>{article.date}</span>
                        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-300">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://mujiaoshi.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: currentCategory ? currentCategory.name : '全部文章',
                item: `https://mujiaoshi.com/category/${currentSlug}`,
              },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: `${currentCategory ? currentCategory.name : '全部文章'} — 牧教师`,
            url: `https://mujiaoshi.com/category/${currentSlug}`,
            description: currentCategory ? currentCategory.description : '浏览所有事奉装备资源',
          }),
        }}
      />
    </>
  );
}